import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

// The walk (docs/designs/dig-walk.md, stages 5a and 5b). Layer 1 comes with
// the page; every later layer is fetched here when the visitor asks for it
// and appended by Turbo, along with its entry in the strata rail and its dot
// on the phone's spine. The trail (every label shown so far) is sent with each
// request, which is what keeps a label from ever showing twice. It lives in
// sessionStorage so a dig belongs to its tab and a reload starts over.
export default class extends Controller {
  static targets = [
    "hops",
    "card",
    "tree",
    "spine",
    "next",
    "retry",
    "edge",
    "depth",
    "bar",
  ];
  static values = { url: String, seed: String };

  connect() {
    this.#saveTrail();
    this.#report("Dig Started");
  }

  // The crate lives in the floating menu, outside this element; the menu
  // listens on window. The card answers on the spot so the click is seen
  // where it happened, not only on the badge at the edge of the page.
  crate({ currentTarget, params: { labelId } }) {
    window.dispatchEvent(new CustomEvent("dig:crate", { detail: { labelId } }));
    currentTarget.disabled = true;
    currentTarget.querySelector("wa-icon")?.setAttribute("name", "check");
    currentTarget.lastChild.textContent = "In the crate";
  }

  // A rail entry opens its layer. Marking the new layer current selects its
  // entry too, which fires this again; the guard keeps that a no-op.
  select({ detail }) {
    const item = detail.selection?.[0];
    if (!item || item.dataset.digDepth === this.current) return;
    this.#focus(item.dataset.digDepth);
  }

  async hop() {
    if (this.busy) return;
    this.busy = true;
    this.retryTargets.forEach((el) => (el.hidden = true));
    this.nextTargets.forEach((button) => (button.loading = true));

    const last = this.hopsTarget.lastElementChild;
    const depth = Number(last.dataset.digDepth) + 1;
    const sources = this.#cardsIn(last).map((card) => card.dataset.digLabel);

    try {
      const appended = this.#nextChild();
      // request.js renders a Turbo Stream answer on its own.
      const response = await post(this.urlValue, {
        body: JSON.stringify({ depth, sources, visited: this.trail }),
        contentType: "application/json",
        responseKind: "turbo-stream",
      });
      if (!response.ok) throw new Error(response.statusCode);

      await this.#land(await appended, depth);
    } catch {
      this.retryTargets.forEach((el) => (el.hidden = false));
    } finally {
      this.busy = false;
      this.nextTargets.forEach((button) => (button.loading = false));
    }
  }

  get trail() {
    return [
      this.seedValue,
      ...this.cardTargets.map((card) => card.dataset.digLabel),
    ];
  }

  // One layer open, its entry and its dot current, the layer in view.
  async #focus(depth) {
    this.current = depth;
    // The layer above folds while the new one lands, which moves everything
    // below it; the scroll waits for that fold to finish.
    const folding = [];
    for (const layer of this.hopsTarget.querySelectorAll("[data-dig-depth]")) {
      const details = layer.querySelector("wa-details");
      if (!details) continue;
      const open = layer.dataset.digDepth === depth;
      if (!open && details.open) {
        folding.push(
          new Promise((done) =>
            details.addEventListener("wa-after-hide", done, { once: true }),
          ),
        );
      }
      details.open = open;
    }
    for (const mark of [
      ...this.treeTarget.querySelectorAll(".dig-stratum"),
      ...this.spineTarget.children,
    ]) {
      const current = mark.dataset.digDepth === depth;
      current
        ? mark.setAttribute("aria-current", "true")
        : mark.removeAttribute("aria-current");
      if ("selected" in mark) mark.selected = current;
      if (mark.tagName === "WA-ICON")
        mark.setAttribute("variant", current ? "solid" : "regular");
    }
    this.depthTarget.textContent = `${depth} ${depth === "1" ? "layer" : "layers"} deep`;
    this.barTarget.textContent = `Layer ${depth}`;
    const target = this.hopsTarget.querySelector(
      `#dig_hop_${depth} wa-details`,
    );
    await Promise.all(folding);
    await target?.updateComplete;
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  // The new layer is in the page: the edge of the map is not a layer, the
  // last real one stays open and there is nothing further to ask for;
  // otherwise the new layer becomes the current one.
  async #land(hop, depth) {
    this.#saveTrail();
    this.#report("Dig Hop", { depth });
    if ("digEdge" in hop.dataset) {
      this.nextTargets.forEach((button) => (button.hidden = true));
      this.edgeTargets.forEach((el) => (el.hidden = false));
    } else {
      await this.#focus(String(depth));
    }
  }

  #cardsIn(layer) {
    return this.cardTargets.filter((card) => layer.contains(card));
  }

  #saveTrail() {
    try {
      sessionStorage.setItem(
        `dig:${this.seedValue}`,
        JSON.stringify(this.trail),
      );
    } catch {}
  }

  // Turbo renders a stream a frame later than it is handed over.
  #nextChild() {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error("no layer rendered")),
        5000,
      );
      const observer = new MutationObserver((mutations) => {
        const node = mutations
          .flatMap((m) => [...m.addedNodes])
          .find((n) => n.nodeType === Node.ELEMENT_NODE);
        if (!node) return;
        clearTimeout(timer);
        observer.disconnect();
        resolve(node);
      });
      observer.observe(this.hopsTarget, { childList: true });
    });
  }

  #report(name, props = {}) {
    try {
      window.plausible?.(name, { props: { seed: this.seedValue, ...props } });
    } catch {}
  }
}
